import { ChatBotType } from "./ChatWidget";

/**
 * CLE-2811 — how the chat widget decides which suggestion chips to show.
 *
 * Three sources, in order. The fallbacks are the questions that were hard-coded into the widget
 * before any of this existed, kept verbatim: a model with nothing configured and a surface that
 * passes nothing must behave exactly as the widget did before, or every existing chatbot silently
 * changes on deploy.
 */
export const CONTRACT_MANAGEMENT_FALLBACK_QUESTIONS = [
  "What's my biggest revenue opportunity right now?",
  "Which payers should I prioritize for contract renegotiation?",
  "Give me specific cost saving recommendations based on my current data",
];

export const PATIENT_FALLBACK_QUESTIONS = [
  "Why do I owe this amount?",
  "What's included in this estimate?",
];

/** Matches the server-side cap in `chat.dto.ts` (MAX_SUGGESTED_PROMPTS). */
export const MAX_SUGGESTED_QUESTIONS = 6;

const normalize = (questions?: string[] | null): string[] =>
  (Array.isArray(questions) ? questions : [])
    .filter((question): question is string => typeof question === "string")
    .map((question) => question.trim())
    .filter(Boolean)
    .slice(0, MAX_SUGGESTED_QUESTIONS);

const fallbackFor = (chatType: ChatBotType | string): string[] => {
  if (chatType === "PatientChatBot") return PATIENT_FALLBACK_QUESTIONS;
  if (chatType === "ContractManagementBot")
    return CONTRACT_MANAGEMENT_FALLBACK_QUESTIONS;
  return [];
};

/**
 * Precedence: the mount surface's own questions, then the model's configured
 * `config.suggestedPrompts`, then the historical hard-coded list for the chat type, then nothing.
 *
 * "Empty" always means "defer to the next source", never "show no chips" — an unconfigured model
 * and a surface that passes no prop are the normal case, not a request for a bare widget.
 */
export function resolveSuggestedQuestions(
  chatType: ChatBotType | string,
  propQuestions?: string[] | null,
  modelQuestions?: string[] | null
): string[] {
  // The patient chatbot is live in production and is explicitly out of scope for CLE-2811. It is
  // frozen to the exact two questions the widget has always shown: neither a mount prop nor a
  // model's config can reach it, so no admin edit and no new surface can change what a patient
  // sees. Remove this short-circuit only as a deliberate decision about the patient bot.
  if (chatType === "PatientChatBot") {
    return PATIENT_FALLBACK_QUESTIONS;
  }

  const fromProp = normalize(propQuestions);
  if (fromProp.length > 0) return fromProp;

  const fromModel = normalize(modelQuestions);
  if (fromModel.length > 0) return fromModel;

  return fallbackFor(chatType);
}
